type ZipLibrary = {
  ArrayBufferReader?: new (buffer: ArrayBuffer) => unknown
  BlobReader?: new (blob: Blob) => unknown
  BlobWriter?: new (contentType?: string) => { getData?: (callback: (blob: Blob) => void) => void }
  ZipReader?: new (reader: unknown, options?: { useWebWorkers?: boolean }) => {
    getEntries(): Promise<ZipLibraryEntry[]>
    close(): Promise<void>
  }
  createReader?: (
    reader: unknown,
    success: (reader: {
      getEntries(callback: (entries: ZipLibraryEntry[]) => void): void
      close?(callback: () => void): void
    }) => void,
    error: (error: unknown) => void
  ) => void
  useWebWorkers?: boolean
}

type ZipLibraryEntry = {
  filename?: string
  name?: string
  directory?: boolean
  uncompressedSize?: number
  size?: number
  getData?: (writer: unknown, callback?: (data: unknown) => void, onprogress?: unknown, onerror?: (error: unknown) => void) => Promise<unknown> | void
}

export type ZipPreviewKind = 'text' | 'json' | 'image' | 'pdf' | null

export type ZipEntry = {
  path: string
  name: string
  directory: boolean
  size: number | null
  level: number
  extension: string
  mimeType: string
  previewKind: ZipPreviewKind
  previewable: boolean
}

export type ZipListOptions = {
  useWebWorkers?: boolean
}

export type ZipReadEntryOptions = ZipListOptions & {
  maxSize?: number
  mimeType?: string
}

const TEXT_EXTENSIONS = new Set([
  'c',
  'conf',
  'cpp',
  'cs',
  'css',
  'csv',
  'formdown',
  'h',
  'html',
  'ini',
  'js',
  'jsonl',
  'log',
  'md',
  'mjs',
  'scss',
  'sh',
  'sql',
  'svg',
  'ts',
  'txt',
  'vue',
  'xml',
  'yaml',
  'yml',
])

const IMAGE_MIME_TYPES: Record<string, string> = {
  apng: 'image/apng',
  avif: 'image/avif',
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
}

const TEXT_MIME_TYPES: Record<string, string> = {
  css: 'text/css',
  csv: 'text/csv',
  html: 'text/html',
  js: 'text/javascript',
  jsonl: 'application/x-ndjson',
  md: 'text/markdown',
  mjs: 'text/javascript',
  svg: 'image/svg+xml',
  ts: 'text/typescript',
  txt: 'text/plain',
  vue: 'text/plain',
  xml: 'application/xml',
  yaml: 'application/yaml',
  yml: 'application/yaml',
}

function getGlobalZip(): ZipLibrary | null {
  const globalObject = typeof globalThis === 'undefined' ? null : globalThis as unknown as { zip?: ZipLibrary }
  return globalObject && globalObject.zip ? globalObject.zip : null
}

function getZipLibrary() {
  const zip = getGlobalZip()

  if (!zip || (!zip.ArrayBufferReader && !zip.BlobReader) || (!zip.ZipReader && !zip.createReader)) {
    return null
  }

  return zip
}

export function isAvailable() {
  return Boolean(getZipLibrary())
}

function createReaderSource(zip: ZipLibrary, buffer: ArrayBuffer) {
  if (zip.ArrayBufferReader) {
    return new zip.ArrayBufferReader(buffer)
  }

  return new zip.BlobReader(new Blob([buffer]))
}

async function withReader<T>(buffer: ArrayBuffer, options: ZipListOptions, callback: (entries: ZipLibraryEntry[]) => Promise<T> | T) {
  const zip = getZipLibrary()

  if (!zip) {
    throw new Error('ZIP library is not available')
  }

  const useWebWorkers = options.useWebWorkers !== false
  const source = createReaderSource(zip, buffer)

  if (zip.ZipReader) {
    const reader = new zip.ZipReader(source, { useWebWorkers })
    try {
      return await callback(await reader.getEntries())
    } finally {
      await reader.close()
    }
  }

  return new Promise<T>((resolve, reject) => {
    const previousUseWebWorkers = zip.useWebWorkers
    zip.useWebWorkers = useWebWorkers

    zip.createReader(source, reader => {
      reader.getEntries(async entries => {
        const finish = () => {
          zip.useWebWorkers = previousUseWebWorkers
        }

        try {
          const result = await callback(entries)

          if (reader.close) {
            reader.close(() => {
              finish()
              resolve(result)
            })
          } else {
            finish()
            resolve(result)
          }
        } catch (error) {
          finish()
          reject(error)
        }
      })
    }, error => {
      zip.useWebWorkers = previousUseWebWorkers
      reject(error)
    })
  })
}

async function readRawEntries(buffer: ArrayBuffer, options: ZipListOptions = {}) {
  return withReader(buffer, options, entries => entries)
}

async function inputToBuffer(input: Blob | ArrayBuffer | Uint8Array) {
  if (input instanceof Blob) {
    return input.arrayBuffer()
  }

  if (input instanceof Uint8Array) {
    const copy = new Uint8Array(input.byteLength)
    copy.set(input)
    return copy.buffer
  }

  return input
}

export async function listEntries(input: Blob | ArrayBuffer | Uint8Array, options: ZipListOptions = {}) {
  const buffer = await inputToBuffer(input)
  const entries = await readRawEntries(buffer, options)
  return normalizeEntries(entries)
}

export async function listUrl(url: string, fetchOptions: RequestInit = {}, options: ZipListOptions = {}) {
  const response = await fetch(url, fetchOptions)

  if (!response.ok) {
    throw new Error(`Could not load ZIP archive: HTTP ${response.status}`)
  }

  return listEntries(await response.arrayBuffer(), options)
}

export async function readEntry(input: Blob | ArrayBuffer | Uint8Array, path: string, options: ZipReadEntryOptions = {}) {
  const normalizedPath = normalizePath(path)
  const buffer = await inputToBuffer(input)

  return withReader(buffer, options, async entries => {
    const entry = entries.find(rawEntry => normalizePath(rawEntry.filename || rawEntry.name || '') === normalizedPath)

    if (!entry || entry.directory) {
      throw new Error(`ZIP entry not found: ${path}`)
    }

    const size = entry.uncompressedSize || entry.size || 0
    if (typeof options.maxSize === 'number' && size > options.maxSize) {
      throw new Error(`ZIP entry is too large: ${path}`)
    }

    return readEntryBlob(entry, options.mimeType || getMimeType(normalizedPath))
  })
}

export async function readUrlEntry(url: string, path: string, fetchOptions: RequestInit = {}, options: ZipReadEntryOptions = {}) {
  const response = await fetch(url, fetchOptions)

  if (!response.ok) {
    throw new Error(`Could not load ZIP archive: HTTP ${response.status}`)
  }

  return readEntry(await response.arrayBuffer(), path, options)
}

async function readEntryBlob(entry: ZipLibraryEntry, mimeType: string) {
  const zip = getZipLibrary()

  if (!zip || !zip.BlobWriter || !entry.getData) {
    throw new Error('ZIP entry extraction is not available')
  }

  const writer = new zip.BlobWriter(mimeType)

  if (entry.getData.length <= 1) {
    const data = await entry.getData(writer)
    return data instanceof Blob ? data : new Blob([data as BlobPart], { type: mimeType })
  }

  return new Promise<Blob>((resolve, reject) => {
    entry.getData(writer, data => {
      if (data instanceof Blob) {
        resolve(data)
        return
      }

      if (writer.getData) {
        writer.getData(resolve)
        return
      }

      resolve(new Blob([data as BlobPart], { type: mimeType }))
    }, undefined)
  })
}

function normalizePath(path: string) {
  return String(path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '')
}

function getExtension(path: string) {
  const name = normalizePath(path).split('/').pop() || ''
  const index = name.lastIndexOf('.')
  return index > 0 && index < name.length - 1 ? name.slice(index + 1).toLowerCase() : ''
}

function getMimeType(path: string) {
  const extension = getExtension(path)
  if (extension === 'json') return 'application/json'
  if (extension === 'pdf') return 'application/pdf'
  if (IMAGE_MIME_TYPES[extension]) return IMAGE_MIME_TYPES[extension]
  if (TEXT_MIME_TYPES[extension]) return TEXT_MIME_TYPES[extension]
  if (TEXT_EXTENSIONS.has(extension)) return 'text/plain'
  return ''
}

function getPreviewKind(path: string, directory: boolean): ZipPreviewKind {
  if (directory) return null

  const extension = getExtension(path)
  const mimeType = getMimeType(path)

  if (extension === 'json' || mimeType === 'application/json' || mimeType.endsWith('+json')) return 'json'
  if (extension === 'pdf' || mimeType === 'application/pdf') return 'pdf'
  if (mimeType.startsWith('image/') && IMAGE_MIME_TYPES[extension]) return 'image'
  if (mimeType.startsWith('text/') || TEXT_EXTENSIONS.has(extension)) return 'text'
  return null
}

export function normalizeEntries(entries: ZipLibraryEntry[]) {
  const nodes = new Map<string, ZipEntry>()

  const addNode = (path: string, directory: boolean, size: number) => {
    const normalized = String(path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, directory ? '/' : '')

    if (!normalized) {
      return
    }

    const clean = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized
    const parts = clean.split('/').filter(Boolean)
    let current = ''

    for (let index = 0; index < parts.length; index++) {
      current = current ? `${current}/${parts[index]}` : parts[index]
      const isLast = index === parts.length - 1
      const isDirectory = !isLast || directory

      if (!nodes.has(current)) {
        const previewKind = getPreviewKind(current, isDirectory)
        nodes.set(current, {
          path: current,
          name: parts[index],
          level: index,
          directory: isDirectory,
          size: isDirectory ? null : size || 0,
          extension: isDirectory ? '' : getExtension(current),
          mimeType: isDirectory ? '' : getMimeType(current),
          previewKind,
          previewable: Boolean(previewKind),
        })
      } else if (isDirectory) {
        nodes.get(current).directory = true
        nodes.get(current).previewKind = null
        nodes.get(current).previewable = false
      } else {
        nodes.get(current).size = size || 0
      }
    }
  }

  for (const entry of entries || []) {
    const name = entry.filename || entry.name || ''
    const directory = entry.directory || String(name).endsWith('/')
    const size = entry.uncompressedSize || entry.size || 0
    addNode(name, directory, size)
  }

  return sortEntriesAsTree(Array.from(nodes.values()))
}

function sortEntriesAsTree(entries: ZipEntry[]) {
  const childrenByParent = new Map<string, ZipEntry[]>()

  for (const entry of entries) {
    const parent = entry.path.split('/').slice(0, -1).join('/')
    const children = childrenByParent.get(parent) || []
    children.push(entry)
    childrenByParent.set(parent, children)
  }

  const sortSiblings = (items: ZipEntry[]) => items.sort((a, b) => {
    if (a.directory !== b.directory) {
      return a.directory ? -1 : 1
    }

    return a.name.localeCompare(b.name)
  })

  const result: ZipEntry[] = []
  const appendChildren = (parent: string) => {
    const children = sortSiblings(childrenByParent.get(parent) || [])

    for (const child of children) {
      result.push(child)

      if (child.directory) {
        appendChildren(child.path)
      }
    }
  }

  appendChildren('')
  return result
}

const Zip = {
  isAvailable,
  listEntries,
  listUrl,
  readEntry,
  readUrlEntry,
  normalizeEntries,
}

export default Zip
